import { createFactory, Component } from 'react';

import _extends from '@babel/runtime/helpers/extends';
import _inheritsLoose from '@babel/runtime/helpers/inheritsLoose';

import shallowEqual from 'shallowequal';

var setStatic = function setStatic(key, value) {
    return function (BaseComponent) {
        BaseComponent[key] = value;

        return BaseComponent;
    };
};

var setDisplayName = function setDisplayName(displayName) {
    return setStatic('displayName', displayName);
};

var getDisplayName = function getDisplayName(Component$$1) {
    if (typeof Component$$1 === 'string') {
        return Component$$1;
    }

    if (!Component$$1) {
        return undefined;
    }

    return Component$$1.displayName || Component$$1.name || 'Component';
};

var wrapDisplayName = function wrapDisplayName(BaseComponent, hocName) {
    return hocName + '(' + getDisplayName(BaseComponent) + ')';
};

var mapProps = function mapProps(propsMapper) {
    return function (BaseComponent) {
        var factory = createFactory(BaseComponent);

        var MapProps = function MapProps(props) {
            return factory(propsMapper(props));
        };

        if (process.env.NODE_ENV !== 'production') {
            return setDisplayName(wrapDisplayName(BaseComponent, 'mapProps'))(MapProps);
        }

        return MapProps;
    };
};

var withProps = function withProps(input) {
    var hoc = mapProps(function (props) {
        return _extends({}, props, typeof input === 'function' ? input(props) : input);
    });

    if (process.env.NODE_ENV !== 'production') {
        return function (BaseComponent) {
            return setDisplayName(wrapDisplayName(BaseComponent, 'withProps'))(hoc(BaseComponent));
        };
    }

    return hoc;
};

var pick = function pick(obj, keys) {
    var result = {};

    for (var i = 0; i < keys.length; i++) {
        var key = keys[i];

        if (obj.hasOwnProperty(key)) {
            result[key] = obj[key];
        }
    }

    return result;
};

var mapValues = function mapValues(obj, func) {
    var result = {};

    for (var key in obj) {
        if (obj.hasOwnProperty(key)) {
            result[key] = func(obj[key], key);
        }
    }

    return result;
};

var withHandlers = function withHandlers(handlers) {
    return function (BaseComponent) {
        var factory = createFactory(BaseComponent);

        var WithHandlers =
            /*#__PURE__*/
            (function (_Component) {
                _inheritsLoose(WithHandlers, _Component);

                function WithHandlers() {
                    var _this;

                    for (var _len = arguments.length, _args = new Array(_len), _key = 0; _key < _len; _key++) {
                        _args[_key] = arguments[_key];
                    }

                    _this = _Component.call.apply(_Component, [this].concat(_args)) || this;
                    _this.handlers = mapValues(
                        typeof handlers === 'function' ? handlers(_this.props) : handlers,
                        function (createHandler) {
                            return function () {
                                var handler = createHandler(_this.props);

                                if (process.env.NODE_ENV !== 'production' && typeof handler !== 'function') {
                                    console.error(
                                        'withHandlers(): Expected a map of higher-order functions. ' +
                                            'Refer to the docs for more info.',
                                    );
                                }

                                return handler.apply(void 0, arguments);
                            };
                        },
                    );
                    return _this;
                }

                var _proto = WithHandlers.prototype;

                _proto.render = function render() {
                    return factory(_extends({}, this.props, this.handlers));
                };

                return WithHandlers;
            })(Component);

        if (process.env.NODE_ENV !== 'production') {
            return setDisplayName(wrapDisplayName(BaseComponent, 'withHandlers'))(WithHandlers);
        }

        return WithHandlers;
    };
};

var defaultProps = function defaultProps(props) {
    return function (BaseComponent) {
        var factory = createFactory(BaseComponent);

        var DefaultProps = function DefaultProps(ownerProps) {
            return factory(ownerProps);
        };

        DefaultProps.defaultProps = props;

        if (process.env.NODE_ENV !== 'production') {
            return setDisplayName(wrapDisplayName(BaseComponent, 'defaultProps'))(DefaultProps);
        }

        return DefaultProps;
    };
};

var withState = function withState(stateName, stateUpdaterName, initialState) {
    return function (BaseComponent) {
        var factory = createFactory(BaseComponent);

        var WithState =
            /*#__PURE__*/
            (function (_Component) {
                _inheritsLoose(WithState, _Component);

                function WithState() {
                    var _this;

                    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
                        args[_key] = arguments[_key];
                    }

                    _this = _Component.call.apply(_Component, [this].concat(args)) || this;
                    _this.state = {
                        stateValue: typeof initialState === 'function' ? initialState(_this.props) : initialState,
                    };

                    _this.updateStateValue = function (updateFn, callback) {
                        return _this.setState(function (_ref) {
                            var stateValue = _ref.stateValue;
                            return {
                                stateValue: typeof updateFn === 'function' ? updateFn(stateValue) : updateFn,
                            };
                        }, callback);
                    };

                    return _this;
                }

                var _proto = WithState.prototype;

                _proto.render = function render() {
                    var _extends2;

                    return factory(
                        _extends(
                            {},
                            this.props,
                            ((_extends2 = {}),
                            (_extends2[stateName] = this.state.stateValue),
                            (_extends2[stateUpdaterName] = this.updateStateValue),
                            _extends2),
                        ),
                    );
                };

                return WithState;
            })(Component);

        if (process.env.NODE_ENV !== 'production') {
            return setDisplayName(wrapDisplayName(BaseComponent, 'withState'))(WithState);
        }

        return WithState;
    };
};

var identity = function identity(Component$$1) {
    return Component$$1;
};

var branch = function branch(test, left, right) {
    if (right === void 0) {
        right = identity;
    }

    return function (BaseComponent) {
        var leftFactory;
        var rightFactory;

        var Branch = function Branch(props) {
            if (test(props)) {
                leftFactory = leftFactory || createFactory(left(BaseComponent));
                return leftFactory(props);
            }

            rightFactory = rightFactory || createFactory(right(BaseComponent));
            return rightFactory(props);
        };

        if (process.env.NODE_ENV !== 'production') {
            return setDisplayName(wrapDisplayName(BaseComponent, 'branch'))(Branch);
        }

        return Branch;
    };
};

var shouldUpdate = function shouldUpdate(test) {
    return function (BaseComponent) {
        var factory = createFactory(BaseComponent);

        var ShouldUpdate =
            /*#__PURE__*/
            (function (_Component) {
                _inheritsLoose(ShouldUpdate, _Component);

                function ShouldUpdate() {
                    return _Component.apply(this, arguments) || this;
                }

                var _proto = ShouldUpdate.prototype;

                _proto.shouldComponentUpdate = function shouldComponentUpdate(nextProps) {
                    return test(this.props, nextProps);
                };

                _proto.render = function render() {
                    return factory(this.props);
                };

                return ShouldUpdate;
            })(Component);

        if (process.env.NODE_ENV !== 'production') {
            return setDisplayName(wrapDisplayName(BaseComponent, 'shouldUpdate'))(ShouldUpdate);
        }

        return ShouldUpdate;
    };
};

var pure = function pure(BaseComponent) {
    var hoc = shouldUpdate(function (props, nextProps) {
        return !shallowEqual(props, nextProps);
    });

    if (process.env.NODE_ENV !== 'production') {
        return setDisplayName(wrapDisplayName(BaseComponent, 'pure'))(hoc(BaseComponent));
    }

    return hoc(BaseComponent);
};

var onlyUpdateForKeys = function onlyUpdateForKeys(propKeys) {
    var hoc = shouldUpdate(function (props, nextProps) {
        return !shallowEqual(pick(nextProps, propKeys), pick(props, propKeys));
    });

    if (process.env.NODE_ENV !== 'production') {
        return function (BaseComponent) {
            return setDisplayName(wrapDisplayName(BaseComponent, 'onlyUpdateForKeys'))(hoc(BaseComponent));
        };
    }

    return hoc;
};

var onlyUpdateForPropTypes = function onlyUpdateForPropTypes(BaseComponent) {
    var propTypes = BaseComponent.propTypes;

    if (process.env.NODE_ENV !== 'production') {
        if (!propTypes) {
            console.error(
                'A component without any `propTypes` was passed to ' +
                    '`onlyUpdateForPropTypes()`. Check the implementation of the ' +
                    ('component with display name "' + getDisplayName(BaseComponent) + '".'),
            );
        }
    }

    var propKeys = Object.keys(propTypes || {});
    var OnlyUpdateForPropTypes = onlyUpdateForKeys(propKeys)(BaseComponent);

    if (process.env.NODE_ENV !== 'production') {
        return setDisplayName(wrapDisplayName(BaseComponent, 'onlyUpdateForPropTypes'))(OnlyUpdateForPropTypes);
    }

    return OnlyUpdateForPropTypes;
};

var compose = function compose() {
    for (var _len = arguments.length, funcs = new Array(_len), _key = 0; _key < _len; _key++) {
        funcs[_key] = arguments[_key];
    }

    return funcs.reduce(
        function (a, b) {
            return function () {
                return a(b.apply(void 0, arguments));
            };
        },
        function (arg) {
            return arg;
        },
    );
};

// Higher-order component helpers

export {
    mapProps,
    withProps,
    withHandlers,
    defaultProps,
    withState,
    branch,
    shouldUpdate,
    pure,
    onlyUpdateForKeys,
    onlyUpdateForPropTypes,
    compose,
    wrapDisplayName,
};
